import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import { Deal, ProductSubscription } from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import { Formik, FormikErrors } from 'formik';
import { EnumUtils, FormikUtils, NumberUtils } from '../../shared/utils';
import { EmptyGuid } from '../../shared/constants';
import { Grid, Skeleton, TableContainer, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import { SuccessFeeTierTableDisplay } from './SuccessFeeTierTableDisplay';
import { QueryKeys, useCreditExplorerProductsQuery } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface ChangeProductDrawerProps {
	onClose: () => void;
	subscription: ProductSubscription | null;
	deal: Deal;
}

export const ChangeProductDrawer: React.FC<ChangeProductDrawerProps> = ({ deal, subscription, onClose }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);
	const productsQuery = useCreditExplorerProductsQuery({
		enabled: subscription != null,
		retry: false,
	});

	const changeProductMutation = useMutation(
		async ({ values }: ChangeSubscriptionProductMutationVariables) => {
			const { data, error } = await getApiResponseData<ProductSubscription>(
				api.patch(CerebroApiEndpoints.Deals.ChangeSubscriptionProduct(deal.guid, subscription?.guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(deal.guid));
				snackbar.enqueueSnackbar('Deal engagement product successfully changed.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	var validateForm = (values: ChangeSubscriptionProductFormValues): ChangeSubscriptionProductFormErrors => {
		const errors: ChangeSubscriptionProductFormErrors = {};

		if (values.productGuid == EmptyGuid) {
			errors.productGuid = 'A product must be selected.';
		}

		return errors;
	};

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Change Engagement Product</CerebroHeading>

			<Formik
				initialValues={{
					productGuid: subscription?.product?.productGuid ?? EmptyGuid,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(changeProductMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						{productsQuery.isLoading && (
							<>
								<Skeleton height={60} />
								<Skeleton height={60} />
								<Skeleton height={60} />
							</>
						)}
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								{productsQuery.isSuccess && (
									<TableContainer>
										<Table>
											<TableHead>
												<TableRow>
													<TableCell>Name</TableCell>
													<TableCell>Platform Access</TableCell>
													<TableCell>Success Fee</TableCell>
													<TableCell></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{productsQuery.data
													.filter(product => !product.isCompanySubscriptionDealProduct)
													.map(product => {
														return (
															<TableRow key={product.productGuid}>
																<TableCell>{product.productName}</TableCell>
																<TableCell>
																	<CerebroText>
																		{NumberUtils.FormatCurrency(product.setupFee)}{' '}
																		{EnumUtils.GetSubscriptionFrequencyDescription(
																			product.subscriptionFrequency
																		)}
																	</CerebroText>
																</TableCell>
																<TableCell>
																	{product.successFeeType == 'Tiered' &&
																	product.successFeeTiers &&
																	product.successFeeTiers.length > 0 ? (
																		<SuccessFeeTierTableDisplay
																			tiers={product.successFeeTiers}
																		/>
																	) : product.successFeeType == 'Flat' ? (
																		<>{product.basisPoints} bps</>
																	) : (
																		<>--</>
																	)}

																	{product.minimumSuccessFee &&
																	product.minimumSuccessFee > 0 ? (
																		<CerebroText>
																			<strong>Minimum Success Fee: </strong>{' '}
																			{NumberUtils.FormatCurrency(
																				product.minimumSuccessFee
																			)}{' '}
																		</CerebroText>
																	) : (
																		<></>
																	)}
																</TableCell>
																<TableCell>
																	{values.productGuid == product.productGuid ? (
																		<CerebroText>Selected</CerebroText>
																	) : (
																		<CerebroSecondaryButton
																			onClick={() => {
																				setFieldValue(
																					'productGuid',
																					product.productGuid
																				);
																			}}
																		>
																			Select
																		</CerebroSecondaryButton>
																	)}
																</TableCell>
															</TableRow>
														);
													})}
											</TableBody>
										</Table>
									</TableContainer>
								)}
								{touched.productGuid && errors.productGuid != null && (
									<CerebroText variant="error">{errors.productGuid}</CerebroText>
								)}
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type ChangeSubscriptionProductFormErrors = FormikErrors<ChangeSubscriptionProductFormValues>;
type ChangeSubscriptionProductMutationVariables =
	FormikUtils.MutateOnSubmitVariables<ChangeSubscriptionProductFormValues>;
interface ChangeSubscriptionProductFormValues {
	productGuid: string;
}
